/**
 * Storage migrations (1.1.0): the list, one migration, its items and the actions, against /api/v1/storage-migrations
 * (keys: queryKeys.migrations(), migration(id), migrationItems(id, status)). Every route needs the admin permission.
 *
 * Start (a move) and Remove sources need the source location's name typed: the body is {confirm: name}, and the API
 * answers 428 CONFIRMATION_REQUIRED without it (isConfirmationRequired tells the dialogs).
 */
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type {
  MigrationBody, MigrationDTO, MigrationItemDTO, MigrationItemStatus, MigrationItemsPage, MigrationPatch, MigrationPlanReport,
  MigrationPreview, MigrationPreviewBody, MigrationStatus,
} from '@prism/contracts';
import { ApiError, api } from '../client';
import { queryKeys } from '../queryKeys';
import { usePaged } from './paged';

const BASE = '/api/v1/storage-migrations';

/** Statuses the scheduler is working on: the views refresh often while any migration is in one. */
export const MIGRATION_BUSY_STATUSES: readonly MigrationStatus[] = ['planning', 'running', 'removing'];
const busy = (m: Pick<MigrationDTO, 'status'> | undefined) => Boolean(m && MIGRATION_BUSY_STATUSES.includes(m.status));

/** 428 CONFIRMATION_REQUIRED: the source location's name was not typed (or not exactly). */
export const isConfirmationRequired = (err: unknown): boolean =>
  err instanceof ApiError && (err.status === 428 || err.code === 'CONFIRMATION_REQUIRED');

export function useMigrations(enabled = true) {
  return useQuery({
    queryKey: queryKeys.migrations(),
    queryFn: () => api<{ items: MigrationDTO[] }>(BASE),
    enabled,
    refetchInterval: (q) => ((q.state.data?.items ?? []).some((m) => busy(m) || m.status === 'paused') ? 5000 : 30_000),
  });
}

export function useMigration(id: string | null | undefined) {
  return useQuery({
    queryKey: queryKeys.migration(id),
    queryFn: () => api<MigrationDTO>(`${BASE}/${id}`),
    enabled: Boolean(id),
    refetchInterval: (q) => (busy(q.state.data) ? 3000 : q.state.data?.status === 'paused' ? 10_000 : false),
  });
}

/** The dry run's report (also on the migration itself as planReport). */
export function useMigrationPlan(id: string | null | undefined, enabled = true) {
  return useQuery({
    queryKey: [...queryKeys.migration(id), 'plan'],
    queryFn: () => api<MigrationPlanReport | null>(`${BASE}/${id}/plan`),
    enabled: Boolean(id) && enabled,
  });
}

/**
 * Keyset pages of items with one status (or all), with Load more. `refresh` (while it runs) refetches every 10 s, but
 * only while one page is loaded: a refetch reloads every loaded page, so after Load more the page offers Refresh.
 */
export function useMigrationItems(id: string | null | undefined, status: MigrationItemStatus | 'all', refresh = false) {
  const qs = status === 'all' ? '' : `?status=${status}`;
  const list = usePaged<MigrationItemDTO, MigrationItemsPage>({
    queryKey: queryKeys.migrationItems(id, status),
    url: `${BASE}/${id}/items${qs}`,
    pageSize: ITEMS_PAGE,
    enabled: Boolean(id),
    refetchInterval: (items) => (refresh && itemPages(items.length) <= 1 ? 10_000 : false),
  });
  return { ...list, pageCount: itemPages(list.data?.items.length ?? 0) };
}

const ITEMS_PAGE = 100;
/** Pages loaded, from the rows: every page but the last is full. */
const itemPages = (rows: number) => Math.ceil(rows / ITEMS_PAGE);

/** The failed and skipped files as CSV (a plain link: the browser downloads it with the session cookie). */
export const migrationItemsCsvUrl = (id: string) => `${BASE}/${id}/items.csv`;

/** Live counts for the wizard's "what" step (POST /preview, 5 s statement timeout: partial when it stopped). */
export function useMigrationPreview(body: MigrationPreviewBody | null) {
  return useQuery({
    queryKey: [...queryKeys.migrations(), 'preview', body],
    queryFn: () => api<MigrationPreview>(`${BASE}/preview`, { method: 'POST', json: body }),
    enabled: Boolean(body),
    staleTime: 30_000,
    retry: false,
  });
}

/** One audit row of a migration's trail. */
export interface MigrationAuditEntry {
  id: string;
  action: string;
  actor: { id: string; displayName: string } | null;
  at: string;
  details: Record<string, unknown> | null;
}

/**
 * The migration's audit trail. Not in the plan's route list (section 8): GET …/:id/audit is asked of the backend
 * stream; until it answers, the detail page leaves the section out (no retries, errors are quiet).
 */
export function useMigrationAudit(id: string | null | undefined) {
  return useQuery({
    queryKey: [...queryKeys.migration(id), 'audit'],
    queryFn: () => api<{ items: MigrationAuditEntry[] }>(`${BASE}/${id}/audit`),
    enabled: Boolean(id),
    retry: false,
  });
}

/** Create, change, delete and every action; each refreshes every migration view and the storage locations. */
export function useMigrationMutations() {
  const qc = useQueryClient();
  const refresh = () => {
    void qc.invalidateQueries({ queryKey: queryKeys.migrations() });
    void qc.invalidateQueries({ queryKey: queryKeys.storage() });
  };
  // Called in a fixed order below, so each action keeps its own mutation state.
  const useAction = <B = Record<string, never>>(path: string) => useMutation({
    mutationFn: ({ id, body }: { id: string; body?: B }) => api<MigrationDTO>(`${BASE}/${id}/${path}`, { method: 'POST', json: body ?? {} }),
    onSuccess: refresh,
  });
  return {
    /** A new migration, saved as a draft. */
    create: useMutation({
      mutationFn: (body: MigrationBody) => api<MigrationDTO>(BASE, { method: 'POST', json: body }),
      onSuccess: refresh,
    }),
    /** 409 MIGRATION_STATE when the change does not apply in the status (while running: name, notes, limits, window). */
    update: useMutation({
      mutationFn: ({ id, body }: { id: string; body: MigrationPatch }) => api<MigrationDTO>(`${BASE}/${id}`, { method: 'PATCH', json: body }),
      onSuccess: refresh,
    }),
    /** Drafts and finished migrations. */
    remove: useMutation({
      mutationFn: (id: string) => api<{ ok: true }>(`${BASE}/${id}`, { method: 'DELETE' }),
      onSuccess: refresh,
    }),
    plan: useAction('plan'),
    /** {confirm: the source's name} for a move; 428 CONFIRMATION_REQUIRED without it; 409 MIGRATION_ACTIVE. */
    start: useAction<{ confirm?: string }>('start'),
    pause: useAction<{ cancelRunning: boolean }>('pause'),
    resume: useAction('resume'),
    cancel: useAction('cancel'),
    retryFailed: useAction('retry-failed'),
    /** Only from copying_done; {confirm: the source's name}. */
    removeSources: useAction<{ confirm?: string }>('remove-sources'),
  };
}
